import { Link } from 'react-router-dom';
import { 
  Wallet, ChartPie, Target, Sparkle, ShieldCheck, CurrencyCircleDollar, ArrowRight
} from '@phosphor-icons/react';

const LOGO_URL = '/logo-square.png';

const features = [
  { icon: Wallet, title: 'Expense Tracking', desc: 'Log income and expenses with categories, notes, and recurring toggles.' },
  { icon: ChartPie, title: 'Budget Planner', desc: 'Set category budgets and see real-time progress with visual warnings.' },
  { icon: Target, title: 'Savings Goals', desc: 'Set targets with deadlines and track how much you need to save weekly.' },
  { icon: Sparkle, title: 'AI Insights', desc: 'Get personalized spending analysis and recommendations powered by AI.' },
  { icon: ShieldCheck, title: 'Affordability Check', desc: 'Ask "Can I afford this?" and get an instant impact analysis.' },
  { icon: CurrencyCircleDollar, title: 'Multi-Currency', desc: 'Track finances in USD, EUR, GBP, INR, and more.' },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0B0F1A] text-[#F9FAFB]">
      {/* Nav */}
      <header className="w-full max-w-5xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between" data-testid="landing-nav">
        <div className="flex items-center gap-2.5">
          <img src={LOGO_URL} alt="UniWallet" className="w-8 h-8" />
          <span className="text-lg font-semibold tracking-tight">
            <span className="gradient-text">Uni</span>Wallet
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-sm text-[#9CA3AF] hover:text-white transition-colors" data-testid="landing-login-btn">
            Log in
          </Link>
          <Link to="/register" className="btn-primary text-sm py-2 px-4" data-testid="landing-signup-btn">
            Sign up free
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="w-full max-w-5xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-20 text-center" data-testid="landing-hero">
        <div className="inline-flex items-center gap-2 bg-[#8B5CF6]/10 border border-[#8B5CF6]/20 rounded-full px-4 py-1.5 mb-6">
          <Sparkle size={14} weight="duotone" className="text-[#8B5CF6]" />
          <span className="text-xs font-medium text-[#8B5CF6]">AI-Powered Finance</span>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
          Smart budgeting for<br />
          <span className="gradient-text">university students</span>
        </h1>
        <p className="mt-5 text-base sm:text-lg text-[#9CA3AF] max-w-xl mx-auto leading-relaxed">
          Track spending, set budgets, save towards goals, and get AI-powered insights — all in one clean app built for student life.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link to="/register" className="btn-ai flex items-center gap-2 text-sm py-3 px-6" data-testid="hero-signup-btn">
            Get started free
            <ArrowRight size={16} weight="bold" />
          </Link>
          <Link to="/about" className="btn-secondary text-sm py-3 px-6" data-testid="hero-about-btn">
            Learn more
          </Link>
        </div>
      </section>

      {/* Features */}
      <section className="w-full max-w-5xl mx-auto px-4 sm:px-6 pb-24" data-testid="landing-features">
        <h2 className="text-lg font-semibold text-center mb-10 text-[#9CA3AF]">Everything you need to manage student finances</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="card-default p-5">
              <div className="w-9 h-9 rounded-lg bg-[#8B5CF6]/10 flex items-center justify-center mb-3">
                <Icon size={18} weight="duotone" className="text-[#8B5CF6]" />
              </div>
              <h3 className="font-semibold text-sm mb-1">{title}</h3>
              <p className="text-xs text-[#6B7280] leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 text-center text-xs text-[#6B7280]" data-testid="landing-footer">
        <div className="flex items-center justify-center gap-4">
          <span>UniWallet</span>
          <Link to="/about" className="hover:text-white transition-colors">About</Link>
          <Link to="/login" className="hover:text-white transition-colors">Login</Link>
          <Link to="/register" className="hover:text-white transition-colors">Sign up</Link>
        </div>
      </footer>
    </div>
  );
}
